import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { useDebouncedSave } from 'hooks/useDebounceSave';

import { IObjectWithId } from 'interfaces/common';

import { getTotalItems } from 'components/_basic/Form/FieldTable/components/FieldTableTotalRow/helpers';
import { LOOK, TotalSumInput } from 'components/_basic/TotalSumInput';
import { ILook } from 'components/_basic/TotalSumInput/types';

import { IFormTableTotalProps } from './types';
import { FORM_TABLE_COLUMN_NAME } from '../../constants';

import styles from './FormTableTotal.module.scss';

export const FormTableTotal: React.FC<IFormTableTotalProps> = ({
  name,
  firstColumnWidth,
  columns,
  expectedTotals,
  disabled,
  onErrorStateChange,
  onTotalCalculate,
}) => {
  const { t } = useTranslation();

  const rows: Record<string, string>[] = useWatch({ name });
  const totalRow = getTotalItems(rows);

  const getClassForColumn = useCallback(
    (columnIndex: number): ILook => {
      const total = totalRow[`${FORM_TABLE_COLUMN_NAME}${columnIndex}`];
      const expected = Number(expectedTotals[columnIndex]);
      return !total || total === expected ? LOOK.SUCCESS : LOOK.FAILED;
    },
    [expectedTotals, totalRow],
  );

  const updateError = () => {
    const foundError = Object.keys(expectedTotals).some(
      (columnIndex) => getClassForColumn(Number(columnIndex)) === LOOK.FAILED,
    );

    if (onErrorStateChange) {
      onErrorStateChange(foundError);
    }
  };

  const handleDebouncedSave = (value: IObjectWithId) => {
    updateError();
    if (onTotalCalculate) {
      onTotalCalculate(value);
    }
  };

  useDebouncedSave({ data: totalRow, saveFunction: handleDebouncedSave, debounceTime: 500 });

  return (
    <Grid container display="flex" alignItems="center" gap={1}>
      <Grid item sx={{ width: 20 }}></Grid>
      <Grid item sx={{ width: firstColumnWidth }} className={styles.summaryTitle}>
        {t('total')}
      </Grid>
      {columns.map((item, index) => (
        <Grid item xs key={item.id}>
          <TotalSumInput
            name={'total' + index}
            value={totalRow[`${FORM_TABLE_COLUMN_NAME}${index}`]}
            look={getClassForColumn(index)}
            disabled={disabled}
            className={styles.totalSumInput}
          />
        </Grid>
      ))}
    </Grid>
  );
};
